import { useEffect, useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { AlertCircle, ExternalLink, Info, Search, Trash2 } from 'lucide-react'
import { useGetAllRequirements } from '@/utils/api/membership-service'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import type { CategoryRequirement } from './types'
import { mapRequirementsResponse } from './utils'

interface RequirementManagerProps {
  requirements: CategoryRequirement[]
  onChange: (requirements: CategoryRequirement[]) => void
  disabled?: boolean
}

const getRequirementKey = (requirement: CategoryRequirement) =>
  requirement.id != null ? requirement.id.toString() : `${requirement.label}`

export function RequirementManager({
  requirements,
  onChange,
  disabled,
}: RequirementManagerProps) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  const requirementsQuery = useGetAllRequirements({
    query: {
      select: (response) => mapRequirementsResponse(response.data),
    },
  })

  const availableRequirements = requirementsQuery.data ?? []

  useEffect(() => {
    if (!dialogOpen) {
      setSearchTerm('')
    }
  }, [dialogOpen])

  const selectedKeys = useMemo(() => {
    return new Set(requirements.map(getRequirementKey))
  }, [requirements])

  const filteredRequirements = useMemo(() => {
    if (!searchTerm.trim()) {
      return availableRequirements
    }

    const term = searchTerm.trim().toLowerCase()

    return availableRequirements.filter((requirement) => {
      const labelMatch = requirement.label.toLowerCase().includes(term)
      const descriptionMatch = requirement.description?.toLowerCase().includes(term)
      const typeMatch = requirement.type?.toLowerCase().includes(term)

      return labelMatch || descriptionMatch || typeMatch
    })
  }, [availableRequirements, searchTerm])

  const handleOpenDialog = () => {
    setDialogOpen(true)
  }

  const handleRemoveRequirement = (requirementId: string) => {
    onChange(requirements.filter((requirement) => getRequirementKey(requirement) !== requirementId))
  }

  const handleToggleRequirement = (requirement: CategoryRequirement, checked: boolean) => {
    if (checked) {
      onChange([...requirements, requirement])
    } else {
      onChange(
        requirements.filter(
          (existing) => getRequirementKey(existing) !== getRequirementKey(requirement),
        ),
      )
    }
  }

  const handleSaveSelection = () => {
    setDialogOpen(false)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-base">Category Requirements</Label>
          <p className="text-sm text-muted-foreground mt-1">
            Select requirements that applicants must fulfill for this category
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleOpenDialog}
            disabled={disabled}
          >
            <AlertCircle className="mr-2 h-4 w-4" />
            Select Requirements
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            asChild
          >
            <Link to="/settings/requirements">
              <ExternalLink className="mr-2 h-4 w-4" />
              Manage
            </Link>
          </Button>
        </div>
      </div>

      {availableRequirements.length === 0 && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            No requirements available.{' '}
            <Link to="/settings/requirements" className="underline font-medium">
              Create requirements first
            </Link>{' '}
            before assigning them to categories.
          </AlertDescription>
        </Alert>
      )}

      {requirements.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-10 text-center">
            <AlertCircle className="h-10 w-10 text-muted-foreground mb-4" />
            <p className="text-sm text-muted-foreground">
              No requirements selected yet. Click "Select Requirements" to add them.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Requirement</TableHead>
                <TableHead className="w-[140px]">Mandatory</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="w-[70px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {requirements.map((requirement) => (
                <TableRow key={getRequirementKey(requirement)}>
                  <TableCell className="font-medium">
                    {requirement.label}
                  </TableCell>
                  <TableCell>
                    {requirement.mandatory ? (
                      <Badge variant="destructive" className="text-xs">
                        Mandatory
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs">
                        Optional
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground whitespace-normal">
                    <Badge variant="outline" className="text-xs">{requirement.type ?? '—'}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => handleRemoveRequirement(getRequirementKey(requirement))}
                      disabled={disabled}
                    >
                      <span className="sr-only">Remove requirement</span>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[800px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Select Requirements</DialogTitle>
            <DialogDescription>
              Choose which requirements applicants must fulfill for this membership category.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {availableRequirements.length === 0 ? (
              <div className="text-center py-8">
                <AlertCircle className="h-10 w-10 text-muted-foreground mx-auto mb-4" />
                <p className="text-sm text-muted-foreground mb-4">
                  No requirements available. Create requirements first.
                </p>
                <Button variant="outline" asChild>
                  <Link to="/settings/requirements">
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Go to Requirements
                  </Link>
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Search requirements..."
                    className="pl-9"
                  />
                </div>

                <div className="rounded-lg border max-h-[50vh] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">
                          <span className="sr-only">Select</span>
                        </TableHead>
                        <TableHead>Requirement</TableHead>
                        <TableHead className="w-[120px]">Mandatory</TableHead>
                        <TableHead>Description</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredRequirements.length === 0 ? (
                        <TableRow>
                          <TableCell className="py-8 text-center text-sm text-muted-foreground" colSpan={4}>
                            No requirements match your search.
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredRequirements.map((requirement) => {
                          const key = getRequirementKey(requirement)
                          const isSelected = selectedKeys.has(key)
                          return (
                            <TableRow
                              key={key}
                              className={cn(
                                'cursor-pointer transition-colors',
                                isSelected && 'bg-primary/5 border-primary',
                              )}
                              onClick={() => handleToggleRequirement(requirement, !isSelected)}
                            >
                              <TableCell>
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={(checked) =>
                                    handleToggleRequirement(requirement, checked as boolean)
                                  }
                                  onClick={(event) => event.stopPropagation()}
                                />
                              </TableCell>
                              <TableCell className="font-medium">
                                {requirement.label}
                              </TableCell>
                              <TableCell>
                                {requirement.mandatory ? (
                                  <Badge variant="destructive" className="text-xs">
                                    Mandatory
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="text-xs">
                                    Optional
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-muted-foreground whitespace-normal">
                                {requirement.description ?? '—'}
                              </TableCell>
                            </TableRow>
                          )
                        })
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSaveSelection}>
              Done ({selectedKeys.size} selected)
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
